import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { encryptData, decryptData } from "@/utils/encryption";

// ─── Shape ────────────────────────────────────────────────────────────────────

export interface StudentPreferences {
  language?: string;
  theme?: string;
  notifications?: boolean;
  country?: string;
  examBody?: string;
  classLevel?: string;
  classes?: string[];
  stream?: "science" | "arts" | "commerce" | "general";
  aiModel?: "haiku" | "sonnet";
}

export interface StudentMetadata {
  onboardingCompleted: boolean;
  profileCompleteness?: number;
  lastAssessmentDate?: string;
}

export interface StudentProfile {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  studentId?: string;
  classLevel?: string;
  avatar?: string;
  schoolId?: string;
  traitProfile?: Record<string, number> | null;
  selectedBroadPaths?: string[];
  activeNodes?: unknown[];
  generalTraits?: unknown[];
  careerInterests?: string[];
  academicResults?: unknown[];
  activityLog?: unknown[];
  traitEvents?: unknown[];
  dailyChallengeHistory?: Record<string, 'correct' | 'wrong'>;
  preferences: StudentPreferences;
  metadata: StudentMetadata;
  engagementData?: {
    loginFrequency?: number;
    sessionDuration?: number;
    lastActive?: string;
  };
}

interface StudentState {
  student: StudentProfile | null;
  isLoading: boolean;

  setStudent: (student: StudentProfile) => void;
  updateStudent: (updates: Partial<StudentProfile>) => void;
  updatePreferences: (prefs: Partial<StudentPreferences>) => void;
  updateMetadata: (meta: Partial<StudentMetadata>) => void;
  setTraitProfile: (profile: Record<string, number>) => void;
  clearStudent: () => void;
  setLoading: (loading: boolean) => void;
}

// ─── Encrypted storage (same as AuthStore) ────────────────────────────────────

const encryptedStorage = {
  getItem: (name: string): string | null => {
    const value = localStorage.getItem(name);
    if (!value) return null;
    return decryptData(value);
  },
  setItem: (name: string, value: string): void => {
    localStorage.setItem(name, encryptData(value));
  },
  removeItem: (name: string): void => {
    localStorage.removeItem(name);
  },
};

// ─── Store ────────────────────────────────────────────────────────────────────

export const StudentStore = create<StudentState>()(
  persist(
    (set) => ({
      student: null,
      isLoading: false,

      setStudent: (student) => set({ student }),

      updateStudent: (updates) =>
        set((state) => ({
          student: state.student ? { ...state.student, ...updates } : null,
        })),

      updatePreferences: (prefs) =>
        set((state) => ({
          student: state.student
            ? { ...state.student, preferences: { ...state.student.preferences, ...prefs } }
            : null,
        })),

      updateMetadata: (meta) =>
        set((state) => ({
          student: state.student
            ? { ...state.student, metadata: { ...state.student.metadata, ...meta } }
            : null,
        })),

      setTraitProfile: (profile) =>
        set((state) => ({
          student: state.student ? { ...state.student, traitProfile: profile } : null,
        })),

      clearStudent: () => set({ student: null }),

      setLoading: (loading) => set({ isLoading: loading }),
    }),
    {
      name: "student-storage",
      storage: createJSONStorage(() => encryptedStorage),
      partialize: (state) => ({ student: state.student }),
    },
  ),
);

// ─── Convenience hooks ────────────────────────────────────────────────────────

export const useStudent = () => StudentStore((s) => s.student);

const EMPTY_PREFS: StudentPreferences = {};
export const useStudentPrefs = () => StudentStore((s) => s.student?.preferences ?? EMPTY_PREFS);
export const useTraitProfile = () => StudentStore((s) => s.student?.traitProfile ?? null);
export const useOnboardingDone = () =>
  StudentStore((s) => s.student?.metadata?.onboardingCompleted === true);
